import { z } from 'zod';
const salonPriceListServiceSchema = z
    .object({
    id: z.string().min(1).max(128),
    name: z.string().trim().min(1).max(100),
    durationMinutes: z.number().int().min(1).max(480),
    priceCents: z.number().int().min(0).max(100_000_000),
})
    .strict();
const salonPriceListBrandingSchema = z
    .object({
    salonName: z.string().trim().max(100),
    tagline: z.string().trim().max(160),
    accentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    logoDataUrl: z
        .string()
        .max(275_000)
        .regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/)
        .optional(),
})
    .strict();
/** Customer-facing data that may be stored behind an anonymous public share URL. */
export const salonPriceListPublicShareSchema = z
    .object({
    v: z.literal(1),
    branding: salonPriceListBrandingSchema,
    services: z.array(salonPriceListServiceSchema).min(1).max(50),
})
    .strict();
export const createSalonPriceListShareRequestSchema = salonPriceListPublicShareSchema;
// =============================================================================
// Service and Product Type Schemas
// =============================================================================
export const serviceTypeSchema = z.enum(['primary', 'addon', 'product']);
export const durationBehaviorSchema = z.enum(['additive', 'concurrent']);
// =============================================================================
// Payment / Deposit Rule Schemas (declared early; used by service schemas below)
// =============================================================================
export const depositRuleTypeSchema = z.enum(['none', 'percentage', 'fixed']);
export const depositRuleSchema = z
    .object({
    type: depositRuleTypeSchema,
    value: z.number().min(0, 'Deposit value cannot be negative'),
})
    .superRefine((data, ctx) => {
    if (data.type === 'percentage' && data.value > 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.too_big,
            maximum: 1,
            type: 'number',
            inclusive: true,
            message: 'Percentage deposit value must be between 0 and 1 (e.g. 0.4 for 40%)',
            path: ['value'],
        });
    }
});
export const updatePaymentSettingsRequestSchema = z.object({
    defaultDepositRule: depositRuleSchema,
});
export const lineItemSchema = z.object({
    serviceId: z.string().min(1, 'Service ID is required'),
    serviceName: z.string().min(1, 'Service name is required'),
    type: serviceTypeSchema,
    quantity: z.number().min(1, 'Quantity must be at least 1'),
    unitPrice: z
        .number()
        .int('Unit price must be integer cents')
        .min(0, 'Unit price cannot be negative'),
    durationMinutes: z.number().min(0, 'Duration cannot be negative'),
    durationBehavior: durationBehaviorSchema,
});
export const userRoleSchema = z.enum(['owner', 'admin', 'provider', 'customer']);
export const providerTypeSchema = z.enum(['employee', 'contractor']);
export const membershipStatusSchema = z.enum([
    'pending',
    'approved',
    'rejected',
    'active',
    'suspended',
]);
export const businessHoursSchema = z.object({
    dayOfWeek: z.number().min(0).max(6),
    isOpen: z.boolean(),
    openTime: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Invalid time format (HH:mm)'),
    closeTime: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Invalid time format (HH:mm)'),
});
export const appointmentStatusSchema = z.enum([
    'scheduled',
    'confirmed',
    'completed',
    'cancelled',
    'no_show',
]);
export const reminderChannelSchema = z.enum(['sms', 'email']);
// =============================================================================
// Public API Schemas
// =============================================================================
export const getAvailabilityRequestSchema = z.object({
    serviceId: z.string().min(1, 'Service ID is required'),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)'),
});
export const devicePlatformSchema = z.enum(['ios', 'android']);
export const upsertDeviceRequestSchema = z.object({
    token: z.string().min(1, 'token is required'),
    platform: devicePlatformSchema,
    businessId: z.string().min(1).optional(),
});
export const createBookingIntentRequestSchema = z.object({
    serviceId: z.string().min(1, 'Service ID is required'),
    lineItems: z.array(lineItemSchema).optional(),
    startTime: z.string().datetime({ message: 'Invalid datetime format' }),
    clientName: z.string().min(1, 'Name is required').max(100, 'Name too long'),
    clientPhone: z.string().min(10, 'Phone number is required').max(20, 'Phone number too long'),
    providerId: z.string().optional(),
    bundleId: z.string().optional(),
    /** Language the client is viewing the booking widget in (e.g. resolved i18next locale, "es-MX"). */
    preferredLanguage: z.string().max(20).optional(),
});
// =============================================================================
// Admin Business Schemas
// =============================================================================
export const createBusinessRequestSchema = z.object({
    name: z.string().min(1, 'Business name is required').max(100, 'Name too long'),
    slug: z
        .string()
        .min(3, 'URL slug must be at least 3 characters')
        .max(50, 'URL slug too long')
        .regex(/^[a-z0-9-]+$/, 'URL slug must be lowercase letters, numbers, and hyphens only'),
    timezone: z.string().min(1, 'Timezone is required'),
    businessHours: z.array(businessHoursSchema).length(7, 'Must provide hours for all 7 days'),
    acceptsEmployees: z.boolean().default(true),
    acceptsContractors: z.boolean().default(true),
    contactName: z.string().max(100, 'Contact name too long').optional(),
    contactPhone: z
        .string()
        .min(7, 'Phone number too short')
        .max(20, 'Phone number too long')
        .optional(),
    contactEmail: z.string().email('Invalid email address').max(255, 'Email too long').optional(),
    streetAddress: z.string().max(100, 'Street address too long').optional(),
    city: z.string().max(50, 'City name too long').optional(),
    state: z.string().max(50, 'State name too long').optional(),
    zipCode: z.string().max(20, 'ZIP code too long').optional(),
});
export const updateBusinessHoursRequestSchema = z.object({
    businessHours: z.array(businessHoursSchema).length(7, 'Must provide hours for all 7 days'),
});
export const updateBusinessSettingsRequestSchema = z.object({
    name: z
        .string()
        .trim()
        .min(1, 'Business name is required')
        .max(100, 'Business name too long')
        .optional(),
    acceptsEmployees: z.boolean().optional(),
    acceptsContractors: z.boolean().optional(),
    bookingPersonality: z
        .enum(['classic', 'tech', 'beauty', 'clean', 'bb', 'adventure', 'dark', 'elegant'])
        .optional(),
    contactName: z.string().max(100, 'Contact name too long').optional(),
    contactPhone: z
        .string()
        .min(7, 'Phone number too short')
        .max(20, 'Phone number too long')
        .optional(),
    contactEmail: z.string().email('Invalid email address').max(255, 'Email too long').optional(),
    streetAddress: z.string().max(100, 'Street address too long').optional(),
    city: z.string().max(50, 'City name too long').optional(),
    state: z.string().max(50, 'State name too long').optional(),
    zipCode: z.string().max(20, 'ZIP code too long').optional(),
    /** Default language for clients with no per-client language signal. */
    defaultClientLanguage: z.enum(['en-US', 'es-MX']).optional(),
});
export const updateAlertPolicyRequestSchema = z.object({
    enabled: z.boolean().optional(),
    slowDayUtilizationPercent: z
        .number()
        .int('Slow-day utilization percent must be a whole number')
        .min(1, 'Slow-day utilization percent must be at least 1')
        .max(100, 'Slow-day utilization percent cannot exceed 100')
        .optional(),
    vipInactivityDays: z
        .number()
        .int('VIP inactivity days must be a whole number')
        .min(1, 'VIP inactivity days must be at least 1')
        .max(365, 'VIP inactivity days cannot exceed 365')
        .optional(),
});
export const snoozeAlertRequestSchema = z.object({
    snoozeDays: z
        .number()
        .int('Snooze days must be a whole number')
        .min(1, 'Snooze days must be at least 1')
        .max(30, 'Snooze days cannot exceed 30'),
});
// =============================================================================
// Cancellation & Rescheduling Policy Schemas (declared early; used by service schemas below)
// =============================================================================
export const cancellationPolicyTypeSchema = z.enum(['full_refund_window', 'tiered', 'flat_fee']);
export const cancellationTierSchema = z.object({
    hoursBeforeStart: z.number().min(0, 'Hours must be non-negative'),
    refundPercent: z
        .number()
        .min(0, 'Refund percent must be between 0 and 100')
        .max(100, 'Refund percent must be between 0 and 100'),
});
export const cancellationPolicySchema = z
    .object({
    type: cancellationPolicyTypeSchema,
    windowHours: z.number().min(0, 'Window hours must be non-negative'),
    tiers: z.array(cancellationTierSchema).optional(),
    feeAmountCents: z.number().int('Fee must be integer cents').min(0).optional(),
    allowReschedule: z.boolean(),
    rescheduleWindowHours: z.number().min(0, 'Reschedule window hours must be non-negative'),
    maxReschedules: z.number().int().min(1).nullable(),
    allowProviderChange: z.boolean(),
})
    .superRefine((data, ctx) => {
    if (data.type === 'tiered' && (!data.tiers || data.tiers.length === 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Tiered policy requires at least one tier',
            path: ['tiers'],
        });
    }
    if (data.type === 'flat_fee' && data.feeAmountCents === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Flat fee policy requires feeAmountCents',
            path: ['feeAmountCents'],
        });
    }
});
export const updateCancellationPolicyRequestSchema = cancellationPolicySchema;
export const createServiceRequestSchema = z.object({
    name: z.string().min(1, 'Service name is required').max(100, 'Name too long'),
    durationMinutes: z.number().min(0, 'Duration cannot be negative').max(480, 'Duration too long'),
    price: z
        .number()
        .int('Price must be integer cents')
        .min(0, 'Price cannot be negative')
        .optional(),
    type: serviceTypeSchema.default('primary'),
    durationBehavior: durationBehaviorSchema.default('additive'),
    description: z.string().max(500, 'Description too long').optional(),
    category: z.string().max(50, 'Category too long').optional(),
    sortOrder: z.number().min(0).optional(),
    compatibleWithServiceIds: z.array(z.string()).optional(),
    providerIds: z.array(z.string()).optional(),
    depositRule: depositRuleSchema.optional(),
    cancellationPolicy: cancellationPolicySchema.optional(),
});
export const updateServiceRequestSchema = z.object({
    name: z.string().min(1, 'Service name is required').max(100, 'Name too long').optional(),
    durationMinutes: z
        .number()
        .min(0, 'Duration cannot be negative')
        .max(480, 'Duration too long')
        .optional(),
    price: z
        .number()
        .int('Price must be integer cents')
        .min(0, 'Price cannot be negative')
        .optional(),
    type: serviceTypeSchema.optional(),
    durationBehavior: durationBehaviorSchema.optional(),
    description: z.string().max(500, 'Description too long').optional(),
    category: z.string().max(50, 'Category too long').optional(),
    sortOrder: z.number().min(0).optional(),
    compatibleWithServiceIds: z.array(z.string()).optional(),
    isActive: z.boolean().optional(),
    providerIds: z.array(z.string()).optional(),
    // null clears an existing per-service override (falls back to business default)
    depositRule: depositRuleSchema.nullable().optional(),
    // null clears a per-service cancellation policy override
    cancellationPolicy: cancellationPolicySchema.nullable().optional(),
});
// =============================================================================
// Link Schemas (Booking Page Links section)
// =============================================================================
export const socialPlatformSchema = z.enum([
    'instagram',
    'tiktok',
    'facebook',
    'x',
    'youtube',
    'linkedin',
    'pinterest',
    'yelp',
    'threads',
    'whatsapp',
]);
export const createLinkRequestSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('social'),
        platform: socialPlatformSchema,
        handle: z.string().trim().min(1, 'Handle is required').max(60, 'Handle too long'),
        sortOrder: z.number().min(0).optional(),
    }),
    z.object({
        type: z.literal('custom'),
        label: z.string().trim().min(1, 'Label is required').max(60, 'Label too long'),
        url: z.string().trim().url('Must be a valid URL').max(500, 'URL too long'),
        sortOrder: z.number().min(0).optional(),
    }),
]);
export const updateLinkRequestSchema = z.object({
    platform: socialPlatformSchema.optional(),
    handle: z.string().trim().min(1, 'Handle is required').max(60, 'Handle too long').optional(),
    label: z.string().trim().min(1, 'Label is required').max(60, 'Label too long').optional(),
    url: z.string().trim().url('Must be a valid URL').max(500, 'URL too long').optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().min(0).optional(),
});
export const reorderLinksRequestSchema = z.object({
    orderedIds: z.array(z.string()).min(1, 'At least one link id is required'),
});
// =============================================================================
// Business Templates Schemas
// =============================================================================
export const localizedTextSchema = z.object({
    en: z.string().min(1, 'English text is required'),
    'es-MX': z.string().optional(),
});
export const businessTemplateServiceSchema = z.object({
    name: localizedTextSchema,
    durationMinutes: z.number().min(0, 'Duration cannot be negative').max(480, 'Duration too long'),
    price: z
        .number()
        .int('Price must be integer cents')
        .min(0, 'Price cannot be negative')
        .optional(),
    description: localizedTextSchema.optional(),
    category: localizedTextSchema.optional(),
    type: serviceTypeSchema.optional(),
    durationBehavior: durationBehaviorSchema.optional(),
});
export const applyBusinessTemplateRequestSchema = z.object({
    templateId: z.string().min(1, 'Template ID is required'),
    selectedServiceNames: z.array(z.string().min(1)).optional(),
});
// =============================================================================
// Admin Appointment Schemas
// =============================================================================
export const getAppointmentsRequestSchema = z.object({
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format'),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format'),
});
export const createAppointmentRequestSchema = z.object({
    clientId: z.string().optional(),
    clientName: z.string().min(1, 'Client name is required'),
    clientPhone: z.string().min(10).optional().or(z.literal('')),
    serviceId: z.string().min(1, 'Service ID is required'),
    providerId: z.string().min(1, 'Provider ID is required').optional(),
    startTime: z.string().datetime({ message: 'Invalid datetime format' }),
    notes: z.string().max(500, 'Notes too long').optional(),
});
export const updateAppointmentRequestSchema = z.object({
    startTime: z.string().datetime({ message: 'Invalid datetime format' }).optional(),
    providerId: z.string().min(1, 'Provider ID is required').optional(),
    status: appointmentStatusSchema.optional(),
    notes: z.string().max(500, 'Notes too long').optional(),
});
export const getClientAppointmentHistoryRequestSchema = z.object({
    limit: z.number().int().min(1).max(100).default(50),
    offset: z.number().int().min(0).default(0),
});
// =============================================================================
// Admin Client Schemas
// =============================================================================
export const clientSegmentSchema = z.enum(['vip', 'frequent', 'lapsed', 'new']);
export const clientTagColorSchema = z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Color must be a valid hex color (e.g. #e74c3c)');
export const createClientTagRequestSchema = z.object({
    name: z.string().min(1, 'Tag name is required').max(50, 'Tag name too long'),
    color: clientTagColorSchema,
});
export const updateClientTagRequestSchema = z.object({
    name: z.string().min(1, 'Tag name is required').max(50, 'Tag name too long').optional(),
    color: clientTagColorSchema.optional(),
});
export const updateClientTagsRequestSchema = z.object({
    tagIds: z.array(z.string().min(1)).max(20, 'Too many tags'),
});
export const clientPreferencesSchema = z.object({
    preferredProviderId: z.string().nullable().optional(),
    contactMethod: z.enum(['email', 'sms', 'both']).nullable().optional(),
    /** System-managed; not admin-writable. Set via STOP/START SMS keywords. */
    smsOptOut: z.boolean().optional(),
    /** System-managed; not admin-writable. Set via Mailgun unsubscribe event. */
    emailOptOut: z.boolean().optional(),
});
/** Allowlist for admin PUT .../preferences — opt-out flags intentionally excluded */
export const updateClientPreferencesRequestSchema = z.object({
    preferredProviderId: z.string().nullable().optional(),
    contactMethod: z.enum(['email', 'sms', 'both']).nullable().optional(),
    preferredLanguage: z.enum(['en-US', 'es-MX']).nullable().optional(),
});
export const clientSortBySchema = z.enum([
    'name',
    'lastVisit',
    'lifetimeValue',
    'daysSinceLastVisit',
    'churnRiskScore',
]);
export const getClientsRequestSchema = z.object({
    search: z.string().optional(),
    limit: z.number().min(1).max(100).default(50),
    offset: z.number().min(0).default(0),
    tagIds: z.array(z.string()).optional(),
    segments: z.array(clientSegmentSchema).optional(),
    sortBy: clientSortBySchema.optional(),
    sortOrder: z.enum(['asc', 'desc']).default('asc'),
});
export const updateClientNotesRequestSchema = z.object({
    notes: z.string().max(2000, 'Notes too long'),
});
export const loyaltyRewardTypeSchema = z.enum(['percentage_discount', 'fixed_discount']);
export const updateLoyaltyProgramSettingsRequestSchema = z.object({
    enabled: z.boolean(),
    pointsPerDollar: z.number().min(0, 'Points per dollar cannot be negative').max(1000),
    rewardThreshold: z.number().int().min(1, 'Reward threshold must be at least 1'),
    rewardType: loyaltyRewardTypeSchema,
    rewardValue: z.number().min(0, 'Reward value cannot be negative'),
    vipBonusMultiplier: z.number().min(1, 'VIP bonus multiplier must be at least 1').max(10),
});
export const getClientLoyaltyHistoryRequestSchema = z.object({
    limit: z.number().min(1).max(100).default(50),
    offset: z.number().min(0).default(0),
});
export const adjustClientLoyaltyBalanceRequestSchema = z.object({
    pointsDelta: z
        .number()
        .int()
        .refine((value) => value !== 0, 'Points delta cannot be zero'),
    reason: z.string().min(1, 'Reason is required').max(250, 'Reason too long'),
});
export const createClientRequestSchema = z.object({
    name: z.string().min(1, 'Name is required').max(100, 'Name too long'),
    phone: z.string().min(1).optional().or(z.literal('')),
    email: z.string().email('Invalid email').optional().or(z.literal('')),
    notes: z.string().max(2000, 'Notes too long').optional(),
    tagIds: z.array(z.string().min(1)).max(20, 'Too many tags').optional(),
    preferredLanguage: z.enum(['en-US', 'es-MX']).optional(),
});
export const importClientsRequestSchema = z.object({
    csv: z.string().min(1, 'CSV content is required').max(2_000_000, 'File too large'),
});
export const importClientRowErrorSchema = z.object({
    row: z.number().int(),
    field: z.string().optional(),
    message: z.string(),
    raw: z.record(z.string()).optional(),
});
export const importClientsResponseSchema = z.object({
    created: z.number().int(),
    updated: z.number().int(),
    skipped: z.number().int(),
    total: z.number().int(),
    errors: z.array(importClientRowErrorSchema),
    createdTagNames: z.array(z.string()),
});
// =============================================================================
// Admin Reminder Policy Schemas
// =============================================================================
export const updateReminderPolicyRequestSchema = z.object({
    confirmationEnabled: z.boolean(),
    reminderEnabled: z.boolean(),
    reminderOffsetMinutes: z.number().min(15).max(10080), // 15 min to 7 days
    channel: reminderChannelSchema,
    templateIds: z.array(z.string()),
});
// =============================================================================
// AI Concierge — Onboarding Interview Schemas
//
// The concierge only ever *drafts*; nothing here writes to a business. Each
// tool's input is validated against these schemas before any executor sees it —
// this is the deterministic guardrail that keeps the LLM off the decision path
// (see RFDs/rfd-7-ai-concierge.md). Field shapes mirror the onboarding wizard's
// draft (apps/web/src/pages/admin/onboarding/types.ts OnboardingDraft), not the
// stricter real API schemas above, since the interview proposes a superset that
// the review UI lets the owner edit before anything is actually applied.
// =============================================================================
const dayOfWeekKeySchema = z.enum([
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
]);
export const conciergeDayHoursSchema = z.object({
    open: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Invalid time format (HH:mm)'),
    close: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Invalid time format (HH:mm)'),
    closed: z.boolean(),
});
export const conciergeServiceDraftSchema = z.object({
    name: z.string().min(1, 'Service name is required').max(100, 'Name too long'),
    durationMinutes: z.number().min(0).max(480).optional(),
    price: z.number().int().min(0).optional(),
    category: z.string().max(50).optional(),
    description: z.string().max(500).optional(),
});
export const proposeBusinessBasicsToolSchema = z.object({
    businessName: z.string().min(1).max(100).optional(),
    businessSlug: z
        .string()
        .min(3)
        .max(50)
        .regex(/^[a-z0-9-]+$/)
        .optional(),
    selectedTimezone: z.string().min(1).optional(),
});
export const proposeServicesToolSchema = z.object({
    services: z.array(conciergeServiceDraftSchema).min(1).max(50),
});
export const proposeBusinessHoursToolSchema = z.object({
    businessHours: z.record(dayOfWeekKeySchema, conciergeDayHoursSchema),
});
export const proposeReminderDefaultsToolSchema = z.object({
    reminderEnabled: z.boolean(),
    reminderHours: z.number().min(1).max(168).optional(),
});
export const selectTemplateToolSchema = z.object({
    templateId: z.enum([
        'salon',
        'barbershop',
        'spa',
        'tattoo-shop',
        'nails',
        'massage-therapy',
        'esthetics',
        'personal-training',
    ]),
});
export const askClarifyingQuestionToolSchema = z.object({
    question: z.string().min(1).max(300),
    /** When the answer is naturally one of a small set of choices (a few open time
     * slots, yes/no, picking among a short list), the model can offer them here so
     * the owner can tap one instead of typing. Omitted for open-ended questions. */
    options: z.array(z.string().min(1).max(60)).min(2).max(4).optional(),
});
/** Shared input schema for read-only concierge tools — they take no arguments; the
 * business is already scoped by the authenticated request, not by anything the model supplies. */
export const noArgsToolSchema = z.object({});
export const conciergeUpcomingScheduleToolSchema = z.object({
    daysAhead: z.number().int().min(1).max(14).default(3),
});
/** YYYY-MM-DD, in the business's local timezone — never UTC. */
const localDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
/** HH:mm 24-hour, in the business's local timezone — never UTC. */
const localTimeSchema = z.string().regex(/^\d{2}:\d{2}$/, 'Expected HH:mm');
export const listProvidersToolSchema = noArgsToolSchema;
export const checkAvailabilityToolSchema = z.object({
    serviceId: z.string().min(1),
    date: localDateSchema,
    providerId: z.string().min(1).optional(),
});
export const findClientToolSchema = z.object({
    query: z.string().min(1).max(200),
});
export const proposeAppointmentToolSchema = z.object({
    clientId: z.string().optional(),
    clientName: z.string().min(1).max(100),
    clientPhone: z.string().min(10).max(20).optional(),
    serviceId: z.string().min(1),
    providerId: z.string().min(1).optional(),
    date: localDateSchema,
    time: localTimeSchema,
    notes: z.string().max(500).optional(),
});
/** What the client review UI actually sees — resolved names for display, plus the
 * server-computed UTC startTime so the browser never has to redo timezone math. */
export const conciergeAppointmentDraftSchema = z.object({
    clientId: z.string().optional(),
    clientName: z.string().min(1).max(100),
    clientPhone: z.string().min(10).max(20).optional(),
    serviceId: z.string().min(1),
    serviceName: z.string().optional(),
    providerId: z.string().optional(),
    providerName: z.string().optional(),
    startTime: z.string().datetime(),
    displayDate: z.string(),
    displayTime: z.string(),
    /** IANA timezone the displayDate/displayTime are local to — needed so the
     * review panel can recompute startTime if the owner edits the date/time. */
    timezone: z.string(),
    notes: z.string().max(500).optional(),
});
export const findClientAppointmentsToolSchema = z.object({
    clientQuery: z.string().min(1).max(200),
});
export const proposeRescheduleAppointmentToolSchema = z.object({
    appointmentId: z.string().min(1),
    date: localDateSchema,
    time: localTimeSchema,
});
/** What the client review UI actually sees for a proposed reschedule. */
export const conciergeRescheduleDraftSchema = z.object({
    appointmentId: z.string().min(1),
    clientName: z.string(),
    serviceName: z.string().optional(),
    previousDisplayDate: z.string(),
    previousDisplayTime: z.string(),
    displayDate: z.string(),
    displayTime: z.string(),
    startTime: z.string().datetime(),
    timezone: z.string(),
});
export const proposeCancelAppointmentToolSchema = z.object({
    appointmentId: z.string().min(1),
    reason: z.string().max(300).optional(),
});
/** What the client review UI actually sees for a proposed cancellation. */
export const conciergeCancellationDraftSchema = z.object({
    appointmentId: z.string().min(1),
    clientName: z.string(),
    serviceName: z.string().optional(),
    displayDate: z.string(),
    displayTime: z.string(),
    reason: z.string().max(300).optional(),
});
export const conciergeDraftProposalSchema = z.object({
    businessName: z.string().min(1).max(100).optional(),
    businessSlug: z
        .string()
        .min(3)
        .max(50)
        .regex(/^[a-z0-9-]+$/)
        .optional(),
    businessType: z.string().max(50).optional(),
    selectedTemplateId: z.string().optional(),
    services: z.array(conciergeServiceDraftSchema).max(50).optional(),
    selectedTimezone: z.string().optional(),
    contactName: z.string().max(100).optional(),
    contactPhone: z.string().max(20).optional(),
    contactEmail: z.string().email().max(255).optional(),
    streetAddress: z.string().max(100).optional(),
    city: z.string().max(50).optional(),
    state: z.string().max(50).optional(),
    zipCode: z.string().max(20).optional(),
    businessHours: z.record(dayOfWeekKeySchema, conciergeDayHoursSchema).optional(),
    reminderEnabled: z.boolean().optional(),
    reminderHours: z.number().min(1).max(168).optional(),
    appointment: conciergeAppointmentDraftSchema.optional(),
    reschedule: conciergeRescheduleDraftSchema.optional(),
    cancellation: conciergeCancellationDraftSchema.optional(),
});
// =============================================================================
// Admin Risk Scoring Policy Schemas
// =============================================================================
export const riskAlertChannelSchema = z.enum(['feed', 'email', 'sms', 'both']);
export const updateRiskScoringPolicyRequestSchema = z.object({
    churnRiskHighThreshold: z
        .number()
        .int('Churn risk threshold must be a whole number')
        .min(0, 'Churn risk threshold cannot be below 0')
        .max(100, 'Churn risk threshold cannot exceed 100')
        .optional(),
    vipInactivityMultiplier: z
        .number()
        .min(1.0, 'VIP multiplier must be at least 1.0')
        .max(5.0, 'VIP multiplier cannot exceed 5.0')
        .optional(),
    slowDayUtilizationPercent: z
        .number()
        .int('Slow-day utilization percent must be a whole number')
        .min(1, 'Slow-day utilization percent must be at least 1')
        .max(100, 'Slow-day utilization percent cannot exceed 100')
        .optional(),
    alertsEnabled: z.boolean().optional(),
    alertChannel: riskAlertChannelSchema.optional(),
});
// =============================================================================
// Admin Follow-Up Policy Schemas
// =============================================================================
export const followUpChannelSchema = z.enum(['sms', 'email', 'both']);
export const updateFollowUpPolicyRequestSchema = z.object({
    enabled: z.boolean(),
    lapsedThresholdDays: z.number().int().min(7).max(365),
    channel: followUpChannelSchema,
    messageTemplate: z.string().max(500, 'Message template too long').optional(),
});
// Bundle manage schemas
export const createBundleRequestSchema = z.object({
    name: z.string().min(1, 'Bundle name is required').max(100, 'Name too long'),
    serviceIds: z.array(z.string().min(1)).min(2, 'Bundle must contain at least 2 services'),
    discountType: z.enum(['percentage', 'fixed_price']),
    discountValue: z.number().min(0, 'Discount cannot be negative'),
});
export const updateBundleRequestSchema = z.object({
    name: z.string().min(1, 'Bundle name is required').max(100, 'Name too long').optional(),
    serviceIds: z
        .array(z.string().min(1))
        .min(2, 'Bundle must contain at least 2 services')
        .optional(),
    discountType: z.enum(['percentage', 'fixed_price']).optional(),
    discountValue: z.number().min(0, 'Discount cannot be negative').optional(),
    isActive: z.boolean().optional(),
});
// Public self-service schemas
export const lookupAppointmentRequestSchema = z.object({
    confirmationCode: z.string().min(1, 'Confirmation code is required'),
    phone: z.string().min(10, 'Phone number is required'),
});
export const selfServiceCancelRequestSchema = z
    .object({
    managementToken: z.string().optional(),
    confirmationCode: z.string().optional(),
    phone: z.string().optional(),
})
    .superRefine((data, ctx) => {
    if (!data.managementToken && (!data.confirmationCode || !data.phone)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Provide either managementToken or confirmationCode + phone',
        });
    }
});
export const selfServiceRescheduleRequestSchema = z
    .object({
    managementToken: z.string().optional(),
    confirmationCode: z.string().optional(),
    phone: z.string().optional(),
    newStartTime: z.string().datetime({ message: 'Invalid datetime format' }),
    newProviderId: z.string().optional(),
})
    .superRefine((data, ctx) => {
    if (!data.managementToken && (!data.confirmationCode || !data.phone)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Provide either managementToken or confirmationCode + phone',
        });
    }
});
// =============================================================================
// Admin Provider Schemas
// =============================================================================
export const createProviderRequestSchema = z.object({
    name: z.string().min(1, 'Name is required').max(100, 'Name too long'),
    email: z.string().email('Invalid email address'),
    phone: z.string().min(10, 'Phone number too short').max(20, 'Phone number too long').optional(),
});
export const updateProviderMembershipRequestSchema = z.object({
    role: userRoleSchema.optional(),
    providerType: providerTypeSchema.optional(),
    status: membershipStatusSchema.optional(),
});
// =============================================================================
// Invite Schemas
// =============================================================================
export const createInviteRequestSchema = z
    .object({
    role: z.enum(['provider', 'admin']).default('provider'),
    providerType: providerTypeSchema.optional(),
    email: z.string().email('Invalid email address').optional(),
    expiresInDays: z.number().min(1).max(30).default(7),
})
    .superRefine((data, ctx) => {
    if (data.role === 'provider' && !data.providerType) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'providerType is required when role is provider',
            path: ['providerType'],
        });
    }
    if (data.role === 'admin' && !data.email) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'email is required when role is admin',
            path: ['email'],
        });
    }
});
// =============================================================================
// Provider Profile Schemas
// =============================================================================
export const updateProviderProfileRequestSchema = z.object({
    displayName: z.string().min(1, 'Display name is required').max(100, 'Name too long').optional(),
    phone: z.string().min(10, 'Phone number too short').max(20, 'Phone number too long').optional(),
    bio: z.string().max(1000, 'Bio too long').optional(),
    avatarUrl: z.string().url('Invalid URL').optional(),
    specialties: z.array(z.string().min(1).max(50)).max(20).optional(),
    title: z.string().max(100, 'Title too long').optional(),
    yearsExperience: z.number().min(0).max(100).optional(),
    certifications: z.array(z.string().min(1).max(200)).max(20).optional(),
    languages: z.array(z.string().min(1).max(50)).max(20).optional(),
    gallery: z.array(z.string().url('Invalid image URL')).max(20).optional(),
});
// =============================================================================
// Provider Rating Schemas
// =============================================================================
export const createProviderRatingRequestSchema = z.object({
    rating: z.number().min(1, 'Rating must be at least 1').max(5, 'Rating cannot exceed 5'),
    comment: z.string().max(500, 'Comment too long').optional(),
});
// =============================================================================
// Provider Schedule Schemas
// =============================================================================
export const timeFormatSchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Invalid time format (HH:mm)');
export const scheduleBreakSchema = z.object({
    dayOfWeek: z.number().min(0).max(6),
    startTime: timeFormatSchema,
    endTime: timeFormatSchema,
    label: z.string().max(100).optional(),
});
export const scheduleExceptionSchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)'),
    type: z.enum(['off', 'custom']),
    daySchedule: z
        .object({
        dayOfWeek: z.number().min(0).max(6),
        isOpen: z.boolean(),
        openTime: timeFormatSchema,
        closeTime: timeFormatSchema,
        breaks: z
            .array(z.object({
            startTime: timeFormatSchema,
            endTime: timeFormatSchema,
        }))
            .optional(),
    })
        .optional(),
});
export const updateProviderScheduleRequestSchema = z.object({
    daySchedules: z.array(businessHoursSchema).optional(),
    breaks: z.array(scheduleBreakSchema).optional(),
    exceptions: z.array(scheduleExceptionSchema).optional(),
    blackoutDates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format')).optional(),
    effectiveFrom: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format')
        .optional(),
});
// =============================================================================
// Provider Capacity Schemas
// =============================================================================
export const providerCapacitySchema = z.object({
    providerId: z.string().min(1, 'Provider ID is required'),
    maxDailyBookings: z.number().min(1).optional(),
    maxWeeklyBookings: z.number().min(1).optional(),
    maxConcurrentBookings: z.number().min(1).default(1),
    bufferMinutes: z.number().min(0).optional(),
    minLeadTimeMinutes: z.number().min(0).optional(),
    maxLeadTimeDays: z.number().min(1).optional(),
});
export const updateProviderCapacitySchema = z.object({
    maxDailyBookings: z.number().min(1).optional(),
    maxWeeklyBookings: z.number().min(1).optional(),
    maxConcurrentBookings: z.number().min(1).optional(),
    bufferMinutes: z.number().min(0).optional(),
    minLeadTimeMinutes: z.number().min(0).optional(),
    maxLeadTimeDays: z.number().min(1).optional(),
});
// =============================================================================
// Provider Skill Schemas
// =============================================================================
export const providerSkillSchema = z.object({
    serviceId: z.string().min(1, 'Service ID is required'),
    proficiencyLevel: z.enum(['beginner', 'intermediate', 'expert']).optional(),
    yearsExperience: z.number().min(0).optional(),
    certified: z.boolean().optional(),
});
export const updateProviderSkillsRequestSchema = z.object({
    skills: z.array(providerSkillSchema).optional(),
});
// =============================================================================
// Admin Member User Update Schema
// =============================================================================
export const adminUpdateMemberUserRequestSchema = z.object({
    name: z.string().trim().min(1).max(255).optional(),
    phone: z.string().trim().max(20).optional(),
});
// =============================================================================
// Messaging Schemas
// =============================================================================
export const messageChannelSchema = z.enum(['sms', 'email']);
export const messageDirectionSchema = z.enum(['inbound', 'outbound']);
export const sendMessageRequestSchema = z.object({
    body: z
        .string()
        .min(1, 'Message body is required')
        .max(1600, 'Message body too long (max 1600 characters)'),
    channel: messageChannelSchema,
});
export const searchMessagesRequestSchema = z.object({
    q: z.string().min(1, 'Search query is required').max(200, 'Search query too long'),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
export const getMessagesRequestSchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
export const globalSearchRequestSchema = z.object({
    q: z.string().trim().min(2, 'Search query must be at least 2 characters').max(200),
    limit: z.coerce.number().int().min(1).max(10).default(5),
});
// =============================================================================
// Business Event Schemas
// =============================================================================
export const businessEventTypeSchema = z.enum([
    'appointment.created',
    'appointment.completed',
    'appointment.cancelled',
    'appointment.no_show',
    'appointment.rescheduled',
    'payment.succeeded',
    'payment.failed',
    'payment.refunded',
    'payment.disputed',
    'message.sent',
    'message.received',
    'client.created',
    'client.tagged',
    'booking.abandoned',
]);
export const businessEventSourceSchema = z.enum(['app', 'stripe', 'square', 'shopify']);
export const businessEventSchema = z.object({
    id: z.string().min(1),
    businessId: z.string().min(1),
    clientId: z.string().optional(),
    type: businessEventTypeSchema,
    source: businessEventSourceSchema,
    externalId: z.string().optional(),
    timestamp: z.number().int().positive(),
    payload: z.record(z.string(), z.unknown()),
    schemaVersion: z.number().int().positive().default(1),
});
export const getClientTimelineRequestSchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(20),
    cursor: z.string().optional(),
    type: businessEventTypeSchema.optional(),
    types: z.array(businessEventTypeSchema).optional(),
    source: businessEventSourceSchema.optional(),
    fromDate: z.coerce.number().int().optional(),
    toDate: z.coerce.number().int().optional(),
});
export const getBusinessEventsRequestSchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(20),
    cursor: z.string().optional(),
    types: z.array(businessEventTypeSchema).optional(),
    clientId: z.string().optional(),
    source: businessEventSourceSchema.optional(),
    startTime: z.coerce.number().int().optional(),
    endTime: z.coerce.number().int().optional(),
});
export const seedEventsRequestSchema = z.object({
    businessId: z.string().min(1, 'Business ID is required'),
    events: z
        .array(z.object({
        type: businessEventTypeSchema,
        source: businessEventSourceSchema.default('app'),
        timestamp: z.coerce.number().int().positive().optional(),
        externalId: z.string().min(1).optional(),
        payload: z.record(z.string(), z.unknown()).default({}),
    }))
        .min(1, 'At least one event is required')
        .max(500, 'Cannot seed more than 500 events at once'),
});
// =============================================================================
// Message Suggestion Schemas (TCRM-71)
// =============================================================================
export const suggestionChannelSchema = z.enum(['sms', 'email', 'both']);
export const suggestionTriggerTypeSchema = z.enum(['churn_risk', 'campaign']);
export const suggestionStatusSchema = z.enum(['pending', 'approved', 'rejected', 'sent']);
export const createSuggestionRequestSchema = z.object({
    clientId: z.string().min(1, 'Client ID is required'),
    channel: suggestionChannelSchema,
    subject: z.string().min(1).max(200).optional(),
    body: z.string().min(1, 'Message body is required').max(2000, 'Body too long'),
    triggerType: suggestionTriggerTypeSchema,
});
export const reviewSuggestionRequestSchema = z.object({
    action: z.enum(['approve', 'reject']),
    body: z.string().min(1).max(2000).optional(),
});
export const bulkReviewRequestSchema = z.object({
    ids: z
        .array(z.string().min(1))
        .min(1, 'At least one suggestion ID required')
        .max(50, 'Max 50 at a time'),
    action: z.enum(['approve', 'reject']),
});
// =============================================================================
// Reports Schemas (TCRM-54)
// =============================================================================
export const reportGranularitySchema = z.enum(['week', 'month']);
export const getNoShowReportRequestSchema = z.object({
    from: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)')
        .optional(),
    to: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)')
        .optional(),
    providerId: z.string().optional(),
    serviceId: z.string().optional(),
    granularity: reportGranularitySchema.optional().default('week'),
});
// =============================================================================
// Retention Reports Schemas (TCRM-52)
// =============================================================================
export const getRetentionReportRequestSchema = z.object({
    from: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)')
        .optional(),
    to: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)')
        .optional(),
    /** Override the lapsed threshold from the business FollowUpPolicy (7–365 days). */
    lapsedThresholdDays: z.coerce.number().int().min(7).max(365).optional(),
});
// =============================================================================
// Routing Schemas (TCRM-58)
// =============================================================================
export const getRouteCandidatesRequestSchema = z.object({
    serviceId: z.string().min(1, 'serviceId is required'),
    startTime: z.string().datetime({ message: 'startTime must be a valid ISO 8601 datetime' }),
    duration: z.coerce.number().int().min(1, 'duration (minutes) must be at least 1'),
    clientId: z.string().optional(),
});
// =============================================================================
// Analytics Event Ingestion Schema
// =============================================================================
export const analyticsEventDataSchema = z.object({
    event_id: z.string().min(1).max(128).optional(),
    name: z.enum([
        'page_view',
        'scroll',
        'click',
        'outbound_click',
        'form_submit',
        'user_engagement',
        'experiment_view',
        'seo_page_view',
        'tool_calculate',
        'tool_result_view',
        'tool_cta_click',
        'tool_view',
        'tool_start',
        'tool_complete',
        'result_share',
        'result_download',
        'account_create',
        'first_activation',
        'setup_wizard_started',
        'setup_wizard_step_viewed',
        'setup_wizard_step_completed',
        'setup_wizard_business_type_selected',
        'setup_wizard_template_applied',
        'setup_wizard_services_configured',
        'setup_wizard_location_added',
        'setup_wizard_hours_configured',
        'setup_wizard_payments_connected',
        'setup_wizard_reminders_enabled',
        'setup_wizard_completed',
        'setup_wizard_abandoned',
    ]),
    path: z.string().max(2048, 'path must not exceed 2KB'),
    params: z.record(z.unknown()).optional(),
    timestamp: z.number().int().positive(),
});
export const analyticsSessionContextSchema = z.object({
    referrer: z.string().max(2048),
    utm_source: z.string().max(256).optional(),
    utm_medium: z.string().max(256).optional(),
    utm_campaign: z.string().max(256).optional(),
    utm_term: z.string().max(256).optional(),
    utm_content: z.string().max(256).optional(),
    landing_page: z.string().max(2048),
    experiment_id: z.string().max(256).optional(),
    variant_id: z.string().max(256).optional(),
    attribution_id: z.string().max(128).optional(),
    attribution_tool_slug: z.string().max(128).optional(),
    attribution_landing_page: z.string().max(2048).optional(),
    attribution_captured_at: z.number().int().positive().optional(),
    traffic_type: z.enum(['external', 'internal']).optional(),
});
export const ingestAnalyticsEventsRequestSchema = z.object({
    visitor_id: z.string().min(1).max(256),
    session_id: z.string().min(1).max(256),
    is_new_visitor: z.boolean(),
    session_context: analyticsSessionContextSchema,
    events: z.array(analyticsEventDataSchema).max(50, 'Maximum 50 events per batch'),
});
//# sourceMappingURL=index.js.map